import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { detectDefaultScript } from "@/lib/scriptDetect";
import { useAuth } from "@/contexts/AuthContext";
import { updateMyPreferences } from "@/lib/apiClient";

/**
 * Global script-preference state. Two values: 'uthmani' (default) and
 * 'indopak'. Persisted to localStorage at `qf:script`. On first load, the
 * default is auto-detected from `navigator.language` (PK/IN/BD/AF or ur/pa/sd
 * → indopak; everyone else → uthmani).
 *
 * Cross-device sync: when a user is logged in, their `script_preference` in
 * the backend `users` collection takes precedence over localStorage. When the
 * user explicitly picks a script, we PATCH `/api/users/me/preferences` so
 * the choice follows them to phone + laptop.
 *
 * The provider also writes `data-script="<value>"` to the <html> element so
 * the global CSS in index.css can switch the .font-quran / .font-mushaf-prose
 * font-family without each component needing to know.
 *
 * `explicitlyChosen` reports whether the user has actively picked (vs auto-
 * detected). Onboarding can use this to show the picker only once.
 */
const ScriptCtx = createContext({
  script: "uthmani",
  setScript: () => {},
  explicitlyChosen: false,
});

const STORAGE_KEY = "qf:script";
const CHOSEN_KEY = "qf:script-chosen";

export function ScriptSettingsProvider({ children }) {
  const { user, setUser } = useAuth();

  // Latest-user ref — the setScript callback may be invoked from a render
  // where `user` was still null (e.g. the Settings page just mounted). Reading
  // through a ref guarantees we PATCH even when the click happens within the
  // first paint after /me resolves.
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const [script, setScriptState] = useState(() => {
    if (typeof window === "undefined") return "uthmani";
    return localStorage.getItem(STORAGE_KEY) || detectDefaultScript();
  });
  const [explicitlyChosen, setChosen] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(CHOSEN_KEY) === "1";
  });

  // Sync the <html data-script> attribute so global CSS rules apply.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-script", script);
    }
  }, [script]);

  // When the user logs in (or /me refreshes), hydrate from their saved
  // backend preference. We only adopt the backend value when it actually
  // differs — and we suppress the auto-PATCH-back side-effect on hydration
  // by reading from the ref instead of triggering setScript.
  const lastHydratedUserId = useRef(null);
  const pendingSyncRef = useRef(null); // remembers a click that happened before user resolved
  useEffect(() => {
    if (!user) {
      lastHydratedUserId.current = null;
      return;
    }
    // If the user picked a script before /me finished loading, flush that
    // intent to the backend NOW that we know who they are.
    if (pendingSyncRef.current && pendingSyncRef.current !== user.script_preference) {
      const val = pendingSyncRef.current;
      pendingSyncRef.current = null;
      updateMyPreferences({ script_preference: val })
        .then((u) => setUser && setUser(u))
        .catch(() => { /* offline — local state still correct */ });
      return; // do NOT hydrate from backend yet (we just wrote it)
    }
    if (lastHydratedUserId.current === user.user_id) return; // already hydrated
    lastHydratedUserId.current = user.user_id;
    const backendPref = user.script_preference;
    if (backendPref === "uthmani" || backendPref === "indopak") {
      if (backendPref !== script) setScriptState(backendPref);
      try {
        localStorage.setItem(STORAGE_KEY, backendPref);
        localStorage.setItem(CHOSEN_KEY, "1");
      } catch (_e) { /* private mode — non-fatal */ }
      setChosen(true);
    }
  }, [user, script, setUser]);

  const setScript = useCallback(async (next, { explicit = true, syncRemote = true } = {}) => {
    if (next !== "uthmani" && next !== "indopak") return;
    setScriptState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      if (explicit) {
        localStorage.setItem(CHOSEN_KEY, "1");
        setChosen(true);
      }
    } catch (_e) { /* private mode / quota — non-fatal */ }

    if (!explicit || !syncRemote) return;

    // Cross-device sync. Read the LATEST user from the ref so first-paint
    // clicks aren't dropped if /me hadn't resolved at the time the closure
    // was constructed.
    const latest = userRef.current;
    if (!latest?.user_id) {
      // Queue the change — the user-hydrate effect will flush it.
      pendingSyncRef.current = next;
      return;
    }
    try {
      const updated = await updateMyPreferences({ script_preference: next });
      if (updated && setUser) setUser(updated);
    } catch (_e) { /* offline / network — local state already updated */ }
  }, [setUser]);

  return (
    <ScriptCtx.Provider value={{ script, setScript, explicitlyChosen }}>
      {children}
    </ScriptCtx.Provider>
  );
}

export const useScriptSettings = () => useContext(ScriptCtx);
